import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Video, Plus, Send, Clock, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function VideoReviewTab() {
  const [videos, setVideos] = useState([]);
  const [athleteNames, setAthleteNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_review");
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const [{ data: vids }, { data: aths }] = await Promise.all([api.get(`/videos${q}`), api.get("/athletes")]);
      setVideos(vids);
      setAthleteNames(Object.fromEntries(aths.map((a) => [a.id, a.name])));
    } catch { toast.error("Failed to load videos."); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setForm = (id, patch) => setForms((p) => ({ ...p, [id]: { mechanics_score: "", coach_notes: "", feedback: [], ...p[id], ...patch } }));
  const addFeedbackRow = (id) => setForm(id, { feedback: [...(forms[id]?.feedback || []), { timestamp: "", note: "" }] });
  const updFeedback = (id, i, key, val) => { const fb = [...(forms[id]?.feedback || [])]; fb[i] = { ...fb[i], [key]: val }; setForm(id, { feedback: fb }); };

  const submit = async (id) => {
    const f = forms[id] || {};
    try {
      await api.patch(`/videos/${id}/review`, {
        feedback: (f.feedback || []).filter((x) => x.timestamp || x.note),
        mechanics_score: f.mechanics_score ? parseFloat(f.mechanics_score) : null,
        coach_notes: f.coach_notes || null,
      });
      toast.success("Feedback sent to athlete.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="video-review-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">Video Review</h1>
      <div className="flex gap-2 mb-6">
        {["pending_review", "reviewed", "all"].map((s) => (
          <button key={s} data-testid={`vr-filter-${s}`} onClick={() => setFilter(s)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${filter === s ? "bg-white border-white text-black" : "border-white/20 text-white/60 hover:text-white"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div> : videos.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="vr-empty">No videos here.</div>
      ) : (
        <div className="space-y-6" data-testid="vr-list">
          {videos.map((v) => {
            const f = forms[v.id] || {};
            return (
              <div key={v.id} data-testid={`vr-${v.id}`} className="bg-[#121212] border border-white/10 p-5 grid lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><Video size={16} className="text-white/60" /><span className="font-bold uppercase tracking-tight">{athleteNames[v.athlete_id] || "Athlete"} · {v.kind}</span></div>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border ${v.status === "reviewed" ? "border-white bg-white/10" : "border-white/25 text-white/60"}`}>{v.status === "reviewed" ? <CheckCircle2 size={11} /> : <Clock size={11} />} {v.status.replace("_", " ")}</span>
                  </div>
                  <video controls src={`${BACKEND}/api/videos/${v.id}/file`} className="w-full max-h-80 bg-black border border-white/10" data-testid={`vr-player-${v.id}`} />
                </div>
                <div>
                  {v.status === "reviewed" ? (
                    <div data-testid={`vr-done-${v.id}`}>
                      {v.mechanics_score != null && <p className="font-mono-accent text-xl font-bold mb-2">Mechanics: {v.mechanics_score}/100</p>}
                      {v.coach_notes && <p className="text-white/70 text-sm mb-3">{v.coach_notes}</p>}
                      {(v.feedback || []).map((fb, i) => <div key={`${fb.timestamp}-${i}`} className="flex gap-3 text-sm mb-1"><span className="font-mono-accent text-white/50">{fb.timestamp}</span><span className="text-white/70">{fb.note}</span></div>)}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Mechanics Score (0-100)</label>
                        <input data-testid={`vr-score-${v.id}`} type="number" min="0" max="100" value={f.mechanics_score || ""} onChange={(e) => setForm(v.id, { mechanics_score: e.target.value })} className={inputCls} /></div>
                      <div><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Coach Notes</label>
                        <textarea data-testid={`vr-notes-${v.id}`} rows={2} value={f.coach_notes || ""} onChange={(e) => setForm(v.id, { coach_notes: e.target.value })} className={inputCls} /></div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5"><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Timestamped Feedback</label>
                          <button data-testid={`vr-add-fb-${v.id}`} onClick={() => addFeedbackRow(v.id)} className="text-white/50 hover:text-white"><Plus size={14} /></button></div>
                        {(f.feedback || []).map((fb, i) => (
                          <div key={i} className="flex gap-2 mb-2">
                            <input placeholder="0:03" data-testid={`vr-fb-time-${v.id}-${i}`} value={fb.timestamp} onChange={(e) => updFeedback(v.id, i, "timestamp", e.target.value)} className="w-20 bg-[#0A0A0A] border border-white/15 px-2 py-2 text-sm outline-none focus:border-white" />
                            <input placeholder="Note" data-testid={`vr-fb-note-${v.id}-${i}`} value={fb.note} onChange={(e) => updFeedback(v.id, i, "note", e.target.value)} className={inputCls} />
                          </div>
                        ))}
                      </div>
                      <button data-testid={`vr-submit-${v.id}`} onClick={() => submit(v.id)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors"><Send size={14} /> Send Feedback</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
